import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button } from '@mui/material';
import { VisitorGroup } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import * as React from 'react';
import { useLayoutEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { isMaxNumberOfUsersReached } from 'utils/VisitorGroup/VisitorGroup';
import Loader from 'v2/components/Loader/Loader';
import SearchableCheckboxList from 'v2/components/SearchableCheckboxList/SearchableCheckboxList';
import useWizardStepContext from 'v2/components/WizardModal/useWizardStepContext';
import { WIZARD_STEP_MIN_HEIGHT } from 'v2/constants';

const FORM_ID = 'add-user-wizard-user-groups-step-form';
const STEP_INDEX = 1;

interface FormValues {
  groupIds: string[];
}

interface Props {
  groups: VisitorGroup[];
  groupsLoading: boolean;
  onProceed: (groupIds: string[]) => void;
}

const UserGroupsStep: React.FC<Props> = ({ groups, groupsLoading, onProceed }) => {
  const { translation } = useTranslation();
  const { goToPrev, goToNext, setFooterContent, steps } = useWizardStepContext();

  const { handleSubmit, control } = useForm<FormValues>({ defaultValues: { groupIds: [] } });

  const onSubmit = handleSubmit((data) => {
    onProceed(data.groupIds);
    goToNext();
  });

  useLayoutEffect(() => {
    setFooterContent(
      STEP_INDEX,
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, gap: 2 }}>
        <Button
          key="user-groups-step-back"
          id="user-groups-back-button"
          variant="outlined"
          type="button"
          onClick={goToPrev}
        >
          {translation.back}
        </Button>
        <Button
          key="user-groups-step-submit"
          id="user-groups-next-button"
          variant="contained"
          type="submit"
          form={FORM_ID}
          endIcon={<ChevronRightIcon />}
        >
          {steps[STEP_INDEX + 1].label}
        </Button>
      </Box>
    );
  }, [goToPrev, setFooterContent, steps, translation]);

  return (
    <Box component="form" id={FORM_ID} onSubmit={onSubmit} noValidate sx={{ minHeight: WIZARD_STEP_MIN_HEIGHT }}>
      {groupsLoading ? (
        <Loader />
      ) : (
        <Controller
          name="groupIds"
          control={control}
          render={({ field }) => (
            <SearchableCheckboxList
              items={groups}
              selectedIds={field.value}
              onChange={field.onChange}
              idPrefix="user-groups-"
              disabledIds={groups.filter(isMaxNumberOfUsersReached).map((group) => group.id)}
              disabledTooltip={translation.group_is_full}
            />
          )}
        />
      )}
    </Box>
  );
};

export default UserGroupsStep;
